import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import LeaderboardTable from './components/LeaderboardTable';
import UserPositionCard from './components/UserPositionCard';
import TimeframeFilter from './components/TimeframeFilter';
import PrivacyToggle from './components/PrivacyToggle';

const SocialLeaderboards = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading } = useAuth();
  const [activeCategory, setActiveCategory] = useState('workouts');
  const [timeframe, setTimeframe] = useState('weekly');
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [userPosition, setUserPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPublic, setIsPublic] = useState(true);

  const categories = [
    {
      id: 'workouts',
      label: 'Workout Performance',
      icon: 'Dumbbell',
      description: 'Total workouts completed'
    },
    {
      id: 'activity',
      label: 'Weekly Activity',
      icon: 'Activity',
      description: 'Active days this week'
    },
    {
      id: 'achievements',
      label: 'Achievement Milestones',
      icon: 'Award',
      description: 'Total achievements earned'
    },
    {
      id: 'consistency',
      label: 'Consistency Streak',
      icon: 'Flame',
      description: 'Consecutive active days'
    }
  ];

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      loadLeaderboardData();
    }
  }, [user, currentTenant, activeCategory, timeframe]);

  const getDateRange = () => {
    const now = new Date();
    let startDate = new Date();

    switch (timeframe) {
      case 'daily':
        startDate?.setHours(0, 0, 0, 0);
        break;
      case 'weekly':
        startDate?.setDate(now?.getDate() - 7);
        break;
      case 'monthly':
        startDate?.setMonth(now?.getMonth() - 1);
        break;
      case 'alltime':
        startDate = new Date('2020-01-01');
        break;
      default:
        startDate?.setDate(now?.getDate() - 7);
    }

    return startDate?.toISOString();
  };

  const loadLeaderboardData = async () => {
    setLoading(true);
    setError('');

    try {
      let startDate = getDateRange();

      if (activeCategory === 'workouts') {
        await loadWorkoutLeaderboard(startDate);
      } else if (activeCategory === 'activity') {
        await loadActivityLeaderboard(startDate);
      } else if (activeCategory === 'achievements') {
        await loadAchievementsLeaderboard();
      } else if (activeCategory === 'consistency') {
        await loadConsistencyLeaderboard();
      }
    } catch (err) {
      setError(err?.message || 'Failed to load leaderboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkoutLeaderboard = async (startDate) => {
    const { data: workoutsData, error: workoutsError } = await supabase
      ?.from('workouts')
      ?.select('user_id, duration_minutes, calories_burned, user_profiles(full_name, avatar_url)')
      ?.eq('tenant_id', currentTenant?.id)
      ?.gte('created_at', startDate);

    if (workoutsError) throw workoutsError;

    // Aggregate by user
    const userStats = {};
    workoutsData?.forEach((workout) => {
      const userId = workout?.user_id;
      if (!userStats?.[userId]) {
        userStats[userId] = {
          userId,
          name: workout?.user_profiles?.full_name || 'Anonymous',
          avatar: workout?.user_profiles?.avatar_url,
          workoutCount: 0,
          totalDuration: 0,
          totalCalories: 0
        };
      }
      userStats[userId].workoutCount += 1;
      userStats[userId].totalDuration += workout?.duration_minutes || 0;
      userStats[userId].totalCalories += workout?.calories_burned || 0;
    });

    // Convert to array and sort
    const leaderboard = Object.values(userStats)?.sort((a, b) => b?.workoutCount - a?.workoutCount)?.map((user, index) => ({
        rank: index + 1,
        userId: user?.userId,
        name: user?.name,
        avatar: user?.avatar,
        primaryStat: user?.workoutCount,
        primaryLabel: 'Workouts',
        secondaryStat: `${Math.round(user?.totalDuration)} min`,
        tertiaryStats: `${Math.round(user?.totalCalories)} cal`,
        change: index < 3 ? 'up' : index > 10 ? 'down' : 'neutral'
      }));

    setLeaderboardData(leaderboard);

    // Find user position
    const userPos = leaderboard?.find((entry) => entry?.userId === user?.id);
    setUserPosition(userPos || null);
  };

  const loadActivityLeaderboard = async (startDate) => {
    const { data: workoutsData, error: workoutsError } = await supabase
      ?.from('workouts')
      ?.select('user_id, created_at, user_profiles(full_name, avatar_url)')
      ?.eq('tenant_id', currentTenant?.id)
      ?.gte('created_at', startDate);

    if (workoutsError) throw workoutsError;

    // Count unique active days per user
    const userStats = {};
    workoutsData?.forEach((workout) => {
      const userId = workout?.user_id;
      const date = new Date(workout?.created_at)?.toDateString();

      if (!userStats?.[userId]) {
        userStats[userId] = {
          userId,
          name: workout?.user_profiles?.full_name || 'Anonymous',
          avatar: workout?.user_profiles?.avatar_url,
          activeDays: new Set(),
          totalWorkouts: 0
        };
      }
      userStats?.[userId]?.activeDays?.add(date);
      userStats[userId].totalWorkouts += 1;
    });

    const leaderboard = Object.values(userStats)?.map((user) => ({
        ...user,
        activeDaysCount: user?.activeDays?.size
      }))?.sort((a, b) => b?.activeDaysCount - a?.activeDaysCount)?.map((user, index) => ({
        rank: index + 1,
        userId: user?.userId,
        name: user?.name,
        avatar: user?.avatar,
        primaryStat: user?.activeDaysCount,
        primaryLabel: 'Active Days',
        secondaryStat: `${user?.totalWorkouts} workouts`,
        tertiaryStats: `${Math.round((user?.activeDaysCount / 7) * 100)}% weekly`,
        change: index < 3 ? 'up' : index > 10 ? 'down' : 'neutral'
      }));

    setLeaderboardData(leaderboard);

    const userPos = leaderboard?.find((entry) => entry?.userId === user?.id);
    setUserPosition(userPos || null);
  };

  const loadAchievementsLeaderboard = async () => {
    // Mock achievements data (in production, this would come from an achievements table)
    const { data: membersData, error: membersError } = await supabase
      ?.from('tenant_members')
      ?.select('user_id, user_profiles(full_name, avatar_url)')
      ?.eq('tenant_id', currentTenant?.id)
      ?.eq('is_active', true);

    if (membersError) throw membersError;

    // Calculate achievement scores based on workouts
    const { data: workoutsData, error: workoutsError } = await supabase
      ?.from('workouts')
      ?.select('user_id')
      ?.eq('tenant_id', currentTenant?.id);

    if (workoutsError) throw workoutsError;

    const userStats = {};
    workoutsData?.forEach((workout) => {
      const userId = workout?.user_id;
      if (!userStats?.[userId]) {
        userStats[userId] = 0;
      }
      userStats[userId] += 1;
    });

    const leaderboard = membersData
      ?.map((member) => {
        const workoutCount = userStats?.[member?.user_id] || 0;
        const achievementScore = Math.floor(workoutCount / 5); // 1 achievement per 5 workouts

        return {
          userId: member?.user_id,
          name: member?.user_profiles?.full_name || 'Anonymous',
          avatar: member?.user_profiles?.avatar_url,
          primaryStat: achievementScore,
          primaryLabel: 'Achievements',
          secondaryStat: `${workoutCount} workouts`,
          tertiaryStats: achievementScore > 10 ? 'Elite' : achievementScore > 5 ? 'Advanced' : 'Beginner',
          change: 'neutral'
        };
      })?.sort((a, b) => b?.primaryStat - a?.primaryStat)?.map((user, index) => ({
        ...user,
        rank: index + 1,
        change: index < 3 ? 'up' : index > 10 ? 'down' : 'neutral'
      }));

    setLeaderboardData(leaderboard);

    const userPos = leaderboard?.find((entry) => entry?.userId === user?.id);
    setUserPosition(userPos || null);
  };

  const loadConsistencyLeaderboard = async () => {
    const { data: workoutsData, error: workoutsError } = await supabase
      ?.from('workouts')
      ?.select('user_id, created_at, user_profiles(full_name, avatar_url)')
      ?.eq('tenant_id', currentTenant?.id)
      ?.order('created_at', { ascending: true });

    if (workoutsError) throw workoutsError;

    // Calculate consistency streaks
    const userStats = {};
    workoutsData?.forEach((workout) => {
      const userId = workout?.user_id;
      const date = new Date(workout?.created_at)?.toDateString();

      if (!userStats?.[userId]) {
        userStats[userId] = {
          userId,
          name: workout?.user_profiles?.full_name || 'Anonymous',
          avatar: workout?.user_profiles?.avatar_url,
          dates: []
        };
      }
      if (!userStats?.[userId]?.dates?.includes(date)) {
        userStats?.[userId]?.dates?.push(date);
      }
    });

    // Calculate current streak
    const leaderboard = Object.values(userStats)?.map((user) => {
        const sortedDates = user?.dates?.sort((a, b) => new Date(b) - new Date(a));
        let streak = 0;
        const today = new Date()?.toDateString();

        for (let i = 0; i < sortedDates?.length; i++) {
          const currentDate = new Date(sortedDates[i]);
          const expectedDate = new Date();
          expectedDate?.setDate(expectedDate?.getDate() - i);

          if (currentDate?.toDateString() === expectedDate?.toDateString()) {
            streak++;
          } else {
            break;
          }
        }

        return {
          userId: user?.userId,
          name: user?.name,
          avatar: user?.avatar,
          primaryStat: streak,
          primaryLabel: 'Day Streak',
          secondaryStat: `${user?.dates?.length} total days`,
          tertiaryStats: streak > 30 ? '🔥 On Fire!' : streak > 7 ? '💪 Strong' : '👍 Good',
          change: 'neutral'
        };
      })?.sort((a, b) => b?.primaryStat - a?.primaryStat)?.map((user, index) => ({
        ...user,
        rank: index + 1,
        change: index < 3 ? 'up' : index > 10 ? 'down' : 'neutral'
      }));

    setLeaderboardData(leaderboard);

    const userPos = leaderboard?.find((entry) => entry?.userId === user?.id);
    setUserPosition(userPos || null);
  };

  const handlePrivacyToggle = async (newValue) => {
    setIsPublic(newValue);
    // In production, save to user preferences
    console.log('Privacy setting updated:', newValue);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading leaderboards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-destructive/10 border border-destructive rounded-lg p-6 text-center">
            <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-4" />
            <p className="text-destructive text-lg font-semibold mb-2">Error Loading Leaderboards</p>
            <p className="text-muted-foreground mb-4">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 md:py-8 pb-24 md:pb-8">
        {/* Header Section */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                Social Leaderboards 🏆
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Compete with members at {currentTenant?.name || 'your gym'}
              </p>
            </div>
            <PrivacyToggle isPublic={isPublic} onToggle={handlePrivacyToggle} />
          </div>

          {/* Timeframe Filter */}
          <TimeframeFilter activeTimeframe={timeframe} onTimeframeChange={setTimeframe} />
        </div>

        {/* Category Tabs */}
        <div className="mb-6">
          <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setActiveCategory(category?.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 rounded-lg border transition-smooth whitespace-nowrap
                  ${activeCategory === category?.id
                    ? 'bg-primary text-primary-foreground border-primary shadow-md'
                    : 'bg-card text-foreground border-border hover:border-primary/50 hover:shadow-sm'
                  }
                `}
              >
                <Icon 
                  name={category?.icon} 
                  size={18} 
                  color={activeCategory === category?.id ? 'currentColor' : 'var(--color-muted-foreground)'} 
                />
                <div className="text-left">
                  <p className="text-sm font-semibold">{category?.label}</p>
                  <p className="text-xs opacity-80">{category?.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* User Position Card */}
        {userPosition && (
          <div className="mb-6">
            <UserPositionCard position={userPosition} totalParticipants={leaderboardData?.length} />
          </div>
        )}

        {/* Leaderboard Table */}
        <div className="bg-card border border-border rounded-lg shadow-md overflow-hidden">
          <div className="p-4 md:p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground">
                Top Performers
              </h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Users" size={16} />
                <span>{leaderboardData?.length} members</span>
              </div>
            </div>
          </div>
          <LeaderboardTable data={leaderboardData} currentUserId={user?.id} />
        </div>

        {/* Motivational Footer */}
        <div className="mt-8 bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-lg p-6 text-center">
          <Icon name="Trophy" size={48} color="var(--color-primary)" className="mx-auto mb-3" />
          <h3 className="text-lg md:text-xl font-heading font-bold text-foreground mb-2">
            Keep Pushing! 💪
          </h3>
          <p className="text-sm md:text-base text-muted-foreground">
            Every workout brings you closer to the top. Stay consistent and watch your ranking climb!
          </p>
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default SocialLeaderboards;